import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, FileCheck2, Loader2, Lock, Trash2, UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useAdicionarTalhoes,
  useEditarFazenda,
  useEditarTalhao,
  useExcluirFazenda,
  useExcluirTalhao,
  useFazenda,
} from '../api';
import { parseKmlFile, type TalhaoImport } from '../kml';
import type { FazendaEditTalhao } from '../schemas';
import { MiniMapaTalhoes } from './MiniMapaTalhoes';
import { Campo, SelectBox, Segmented } from './cadastro-ui';
import { inputCls, UFS } from './cadastro-helpers';

function fmtMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

// Estado editável de um talhão já existente (prefill do backend).
interface TalhaoEdit {
  id: number;
  numero: string;
  nome: string;
  area_ha: number;
  tem_visitas: boolean;
}

// Tela · Editar fazenda: altera nome/município/UF/tipo, renomeia talhões e ajusta
// área, exclui talhão sem visita e anexa talhões de um KML atualizado.
export function EditarFazendaView({ imovelId }: { imovelId: number }) {
  const navigate = useNavigate();
  const { data: fazenda, isLoading } = useFazenda(imovelId);
  const editarFazenda = useEditarFazenda(imovelId);
  const editarTalhao = useEditarTalhao(imovelId);
  const excluirTalhao = useExcluirTalhao(imovelId);
  const excluirFazenda = useExcluirFazenda();
  const adicionarTalhoes = useAdicionarTalhoes(imovelId);

  const [nome, setNome] = useState('');
  const [posse, setPosse] = useState<'propria' | 'arrendada'>('propria');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('RO');
  const [talhoes, setTalhoes] = useState<TalhaoEdit[]>([]);
  const [novos, setNovos] = useState<TalhaoImport[]>([]);
  const [arquivo, setArquivo] = useState<{ nome: string; bytes: number } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Prefill quando os dados chegam (uma vez).
  useEffect(() => {
    if (!fazenda) return;
    setNome(fazenda.nome ?? '');
    setPosse(fazenda.arrendada ? 'arrendada' : 'propria');
    setMunicipio(fazenda.municipio ?? '');
    setUf(fazenda.uf ?? 'RO');
    setTalhoes(
      fazenda.talhoes.map((t: FazendaEditTalhao) => ({
        id: t.id,
        numero: t.numero,
        nome: t.nome ?? '',
        area_ha: t.area_ha ?? 0,
        tem_visitas: t.tem_visitas,
      })),
    );
  }, [fazenda]);

  function patchTalhao(id: number, p: Partial<TalhaoEdit>) {
    setTalhoes((x) => x.map((t) => (t.id === id ? { ...t, ...p } : t)));
  }
  function patchNovo(i: number, p: Partial<TalhaoImport>) {
    setNovos((x) => x.map((t, idx) => (idx === i ? { ...t, ...p } : t)));
  }

  function importarKml() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz';
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return;
      setErro(null);
      void parseKmlFile(f)
        .then((parsed) => {
          if (!parsed.length) {
            setErro('Nenhum polígono encontrado no arquivo.');
            return;
          }
          setNovos(parsed.map((t) => ({ ...t, nome: /^T\d+$/.test(t.numero) ? '' : t.numero })));
          setArquivo({ nome: f.name, bytes: f.size });
        })
        .catch(() => setErro('Não foi possível ler o arquivo KML/KMZ.'));
    };
    input.click();
  }

  async function excluir(t: TalhaoEdit) {
    if (t.tem_visitas || excluirTalhao.isPending) return;
    const rotulo = t.nome || `Talhão ${t.numero}`;
    if (!window.confirm(`Excluir ${rotulo}? Esta ação não pode ser desfeita.`)) return;
    setErro(null);
    try {
      await excluirTalhao.mutateAsync(t.id);
      setTalhoes((x) => x.filter((o) => o.id !== t.id));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao excluir o talhão.');
    }
  }

  async function salvar() {
    if (!nome.trim() || salvando) return;
    setErro(null);
    setSalvando(true);
    try {
      // 1) Campos da fazenda (só o que mudou).
      const patch: Record<string, unknown> = {};
      if (nome.trim() !== (fazenda?.nome ?? '')) patch.nome = nome.trim();
      if ((municipio.trim() || null) !== (fazenda?.municipio ?? null)) patch.municipio = municipio.trim() || null;
      if (uf !== (fazenda?.uf ?? 'RO')) patch.uf = uf;
      if ((posse === 'arrendada') !== !!fazenda?.arrendada) patch.tipo_posse = posse;
      if (Object.keys(patch).length) await editarFazenda.mutateAsync(patch);

      // 2) Talhões existentes que mudaram (nome/área).
      const orig = new Map((fazenda?.talhoes ?? []).map((t) => [t.id, t]));
      for (const t of talhoes) {
        const o = orig.get(t.id);
        if (!o) continue;
        const body: { nome?: string | null; area_ha?: number | null } = {};
        if (t.nome.trim() !== (o.nome ?? '')) body.nome = t.nome.trim() || null;
        if (Math.round(t.area_ha) !== Math.round(o.area_ha ?? 0)) body.area_ha = t.area_ha;
        if (Object.keys(body).length) await editarTalhao.mutateAsync({ id: t.id, body });
      }

      // 3) Talhões novos importados do KML.
      if (novos.length) {
        await adicionarTalhoes.mutateAsync(
          novos.map((t) => ({
            geojson: t.geojson,
            area_ha: t.area_ha,
            ...(t.nome ? { nome: t.nome } : {}),
          })),
        );
      }

      // Volta no histórico (a tela da fazenda já está logo abaixo da edição):
      // evita empilhar e faz o "Voltar" da fazenda seguir pra listagem do produtor.
      window.history.back();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar as alterações.');
    } finally {
      setSalvando(false);
    }
  }

  async function excluirFaz() {
    if (excluirFazenda.isPending) return;
    const rotulo = fazenda?.nome || 'esta fazenda';
    const nt = talhoes.length;
    const detalhe = nt ? ` e seus ${nt} ${nt === 1 ? 'talhão' : 'talhões'} (com visitas, fotos e amostras)` : '';
    if (!window.confirm(`Excluir a fazenda "${rotulo}"${detalhe}?\n\nEsta ação não pode ser desfeita.`)) return;
    setErro(null);
    try {
      await excluirFazenda.mutateAsync(imovelId);
      const cpf = fazenda?.agro_produtor_cpf?.replace(/\D/g, '');
      if (cpf) void navigate({ to: '/agronomia/produtores/$cpf', params: { cpf } });
      else void navigate({ to: '/agronomia' });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao excluir a fazenda.');
    }
  }

  const totalNovoHa = novos.reduce((s, t) => s + (t.area_ha || 0), 0);
  const podeSalvar = nome.trim().length > 1 && !isLoading && !salvando;

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex items-center gap-1 px-3 pt-4 pb-2 shrink-0">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
          aria-label="Voltar"
        >
          <ChevronLeft size={26} strokeWidth={2} />
        </button>
        <h1 className="text-[1.35rem] font-bold leading-tight tracking-tight text-text">Editar fazenda</h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-text-muted">Carregando…</p>
        ) : !fazenda ? (
          <p className="py-10 text-center text-sm text-text-muted">Fazenda não encontrada.</p>
        ) : (
          <>
            <div className="flex flex-col gap-4 pt-2">
              <Campo label="Nome da fazenda">
                <input
                  className={inputCls}
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Fazenda Vilhena"
                  autoCapitalize="words"
                />
              </Campo>

              <Campo label="Tipo de área">
                <Segmented
                  value={posse}
                  onChange={setPosse}
                  options={[
                    { value: 'propria', label: 'Própria' },
                    { value: 'arrendada', label: 'Arrendamento' },
                  ]}
                />
              </Campo>

              <div className="grid grid-cols-[1fr_96px] gap-3">
                <Campo label="Município">
                  <input
                    className={inputCls}
                    value={municipio}
                    onChange={(e) => setMunicipio(e.target.value)}
                    placeholder="Vilhena"
                    autoCapitalize="words"
                  />
                </Campo>
                <Campo label="UF">
                  <SelectBox value={uf} onChange={setUf} options={UFS} />
                </Campo>
              </div>
            </div>

            {/* talhões existentes */}
            <p className="pb-2 pt-6 text-[0.72rem] font-semibold uppercase tracking-wider text-accent">
              Talhões ({talhoes.length})
            </p>
            <div className="flex flex-col gap-2">
              {talhoes.map((t) => (
                <div key={t.id} className="flex items-center gap-2.5">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#fde3d5] text-[0.8rem] font-semibold text-accent">
                    {t.numero}
                  </span>
                  <input
                    className={cn(inputCls, 'min-w-0 flex-1 py-2.5')}
                    value={t.nome}
                    onChange={(e) => patchTalhao(t.id, { nome: e.target.value })}
                    placeholder={`Talhão ${t.numero}`}
                  />
                  <div className="relative w-[84px] shrink-0">
                    <input
                      className={cn(inputCls, 'py-2.5 pr-7')}
                      value={String(Math.round(t.area_ha || 0))}
                      onChange={(e) => patchTalhao(t.id, { area_ha: Number(e.target.value.replace(/\D/g, '')) || 0 })}
                      inputMode="numeric"
                      aria-label={`Área do talhão ${t.numero} em hectares`}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[0.8rem] text-text-muted">
                      ha
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void excluir(t)}
                    disabled={t.tem_visitas || excluirTalhao.isPending}
                    aria-label={t.tem_visitas ? 'Talhão com visita — não pode excluir' : `Excluir talhão ${t.numero}`}
                    title={t.tem_visitas ? 'Talhão com visita registrada — exclua as visitas antes' : 'Excluir talhão'}
                    className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-muted disabled:opacity-40 hover:bg-surface2 hover:text-[#b3261e] disabled:hover:bg-transparent disabled:hover:text-text-muted"
                  >
                    {t.tem_visitas ? <Lock size={15} strokeWidth={1.75} /> : <Trash2 size={16} strokeWidth={1.75} />}
                  </button>
                </div>
              ))}
              {talhoes.length === 0 && (
                <p className="py-2 text-[0.82rem] text-text-2">Nenhum talhão cadastrado.</p>
              )}
            </div>

            <div className="my-5 h-px bg-[var(--agro-card-border)]" />

            {/* upload de KML atualizado (anexa talhões novos) */}
            <p className="pb-2 text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
              Adicionar talhões (KML / KMZ atualizado)
            </p>
            {arquivo ? (
              <div className="flex items-center gap-3 rounded-[14px] bg-[#dcefdf] px-3.5 py-3">
                <FileCheck2 size={22} strokeWidth={1.75} className="shrink-0 text-[#1d5a33]" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[0.9rem] font-semibold text-[#1d5a33]">{arquivo.nome}</span>
                  <span className="text-[0.78rem] text-[#3c7a4e]">Importado · {fmtMb(arquivo.bytes)}</span>
                </div>
                <button
                  type="button"
                  onClick={importarKml}
                  className="shrink-0 rounded-full border border-[#1d5a33]/40 px-3.5 py-1.5 text-[0.8rem] font-semibold text-[#1d5a33]"
                >
                  Trocar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={importarKml}
                className="flex w-full flex-col items-center justify-center gap-1.5 rounded-[14px] border-[1.5px] border-dashed border-[var(--agro-card-border)] bg-surface2 px-4 py-6 text-center"
              >
                <UploadCloud size={24} strokeWidth={1.5} className="text-accent" />
                <span className="text-[0.9rem] font-semibold text-text">Importar KML / KMZ</span>
                <span className="text-[0.78rem] text-text-2">Os polígonos entram como talhões novos</span>
              </button>
            )}

            {novos.length > 0 && (
              <>
                <div className="mt-3 h-36 overflow-hidden rounded-[14px] bg-[#37431f]">
                  <MiniMapaTalhoes talhoes={novos} />
                </div>
                <div className="flex items-center justify-between px-1 pb-2 pt-3">
                  <span className="text-[0.9rem] font-semibold text-text">
                    {novos.length} {novos.length === 1 ? 'talhão a adicionar' : 'talhões a adicionar'}
                  </span>
                  <span className="text-[0.9rem] font-semibold text-accent">
                    {Math.round(totalNovoHa).toLocaleString('pt-BR')} ha
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {novos.map((t, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#dcefdf] text-[0.8rem] font-semibold text-[#1d5a33]">
                        +
                      </span>
                      <input
                        className={cn(inputCls, 'min-w-0 flex-1 py-2.5')}
                        value={t.nome}
                        onChange={(e) => patchNovo(i, { nome: e.target.value })}
                        placeholder={`Novo talhão ${i + 1}`}
                      />
                      <div className="relative w-[84px] shrink-0">
                        <input
                          className={cn(inputCls, 'py-2.5 pr-7')}
                          value={String(Math.round(t.area_ha || 0))}
                          onChange={(e) => patchNovo(i, { area_ha: Number(e.target.value.replace(/\D/g, '')) || 0 })}
                          inputMode="numeric"
                          aria-label={`Área do novo talhão ${i + 1} em hectares`}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[0.8rem] text-text-muted">
                          ha
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNovos((x) => x.filter((_, idx) => idx !== i))}
                        aria-label={`Remover novo talhão ${i + 1}`}
                        className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface2 hover:text-[#b3261e]"
                      >
                        <Trash2 size={16} strokeWidth={1.75} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {erro && <p className="mt-3 text-[0.82rem] text-[#b3261e]">{erro}</p>}

            <button
              type="button"
              onClick={() => void salvar()}
              disabled={!podeSalvar}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3.5 text-[0.95rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
            >
              {salvando && <Loader2 size={17} strokeWidth={2} className="animate-spin" />}
              Salvar alterações
            </button>

            <button
              type="button"
              onClick={() => void excluirFaz()}
              disabled={excluirFazenda.isPending}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#e6b8b3] py-3 text-[0.9rem] font-medium text-[#b3261e] hover:bg-[#fbeceb] disabled:opacity-50 dark:border-[#5a2a26] dark:hover:bg-[#3a1f1d]"
            >
              {excluirFazenda.isPending ? (
                <Loader2 size={16} strokeWidth={2} className="animate-spin" />
              ) : (
                <Trash2 size={16} strokeWidth={1.75} />
              )}
              Excluir fazenda
            </button>
          </>
        )}
      </div>
    </div>
  );
}
